import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, Buildings, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Compliance() {
  const [data, setData] = useState({ frameworks: [], industries: [], regions: [] });
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/compliance/frameworks"), api.get("/compliance/tenant")]).then(([f, c]) => {
      setData(f.data); setConfig(c.data.config);
    });
  }, []);

  const toggleFramework = (id) => {
    setConfig({ ...config, frameworks: config.frameworks.includes(id) ? config.frameworks.filter((x) => x !== id) : [...config.frameworks, id] });
  };

  const toggleRegion = (id) => {
    setConfig({ ...config, regions: config.regions.includes(id) ? config.regions.filter((x) => x !== id) : [...config.regions, id] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/compliance/tenant", config);
      toast.success("Compliance configuration saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!config) return <PageShell title="Compliance"><div className="h-64 glass animate-pulse" /></PageShell>;

  // Recommendations = union of industry defaults + all currently-selected regions' frameworks
  const industryDef = data.industries.find((i) => i.id === config.industry);
  const regionFrameworks = data.regions
    .filter((r) => config.regions.includes(r.id))
    .flatMap((r) => r.compliance || []);
  const recommendedIds = new Set([
    ...(industryDef?.default_compliance || []),
    ...regionFrameworks,
  ]);

  return (
    <PageShell
      title="Compliance & Location"
      subtitle="Industry, regions, and applicable frameworks"
      right={<Button onClick={save} disabled={saving} data-testid="save-compliance-btn" className="gap-2"><Sparkle size={14} /> {saving ? "Saving…" : "Save"}</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <GlassCard className="p-6 lg:col-span-4">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={18} weight="duotone" className="text-[var(--accent)]" />
            <h3 className="text-base font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Industry</h3>
          </div>
          <Select value={config.industry} onValueChange={(v) => setConfig({ ...config, industry: v })}>
            <SelectTrigger data-testid="industry-select" className="glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.industries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-[var(--text-3)] mt-3">
            Your industry determines default templates, clause libraries, and suggested compliance frameworks.
          </div>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-8">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={18} weight="duotone" className="text-[var(--accent)]" />
            <h3 className="text-base font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Operating regions</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {data.regions.map((r) => {
              const on = config.regions.includes(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggleRegion(r.id)}
                  data-testid={`region-${r.id}`}
                  className={`text-start p-3 rounded-md border transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{r.name}</span>
                    {on && <CheckCircle size={12} weight="fill" className="text-emerald-400" />}
                  </div>
                  <div className="text-[10px] text-[var(--text-3)] mt-1 font-mono">{r.compliance.join(", ").toUpperCase()}</div>
                </button>
              );
            })}
          </div>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-[var(--accent)]" />
              <h3 className="text-base font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Applicable compliance frameworks</h3>
            </div>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="frameworks-show-all-toggle"
              className={`text-[11px] px-3 py-1.5 rounded-md border transition ${showAll ? "bg-[var(--surface)] border-[var(--border)] text-[var(--text-3)] hover:text-[var(--text-2)]" : "bg-[var(--surface-strong)] border-[var(--accent)] text-[var(--accent)]"}`}
            >
              {showAll ? "Showing all" : "Recommended only"}
            </button>
          </div>

          {!showAll && recommendedIds.size > 0 && (
            <div className="mb-3 text-xs text-[var(--text-3)]">
              Filtered by your industry (<span className="font-mono text-[var(--text-2)]">{config.industry}</span>) and {config.regions.length} region{config.regions.length === 1 ? "" : "s"}. Toggle "Showing all" to see every framework Clariva supports.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.frameworks
              .filter((f) => showAll || recommendedIds.has(f.id))
              .map((f, i) => {
                const on = config.frameworks.includes(f.id);
                const isRecommended = recommendedIds.has(f.id);
                return (
                  <motion.button
                    key={f.id}
                    onClick={() => toggleFramework(f.id)}
                    data-testid={`framework-${f.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`text-start p-4 rounded-lg border transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-semibold">{f.name}</div>
                      {on && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="chip chip-neutral">{f.region}</span>
                      <span className="chip chip-neutral">{f.industry}</span>
                      {isRecommended && <span className="chip chip-high text-[10px]">Recommended</span>}
                    </div>
                    <div className="text-xs text-[var(--text-2)] leading-relaxed">{f.description}</div>
                  </motion.button>
                );
              })}
          </div>
        </GlassCard>
      </div>
    </PageShell>
  );
}
